class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // 缓存推荐容器的内容
    this.cachedRecommendations = null;
    this.recommendationsInitialized = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

    // 延迟设置购物车图标事件，确保DOM完全加载
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setHeaderCartIconAccessibility();
        this.setupCartUpdateListeners();
      });
    } else {
      this.setHeaderCartIconAccessibility();
      this.setupCartUpdateListeners();
    }
  }

  setupCartUpdateListeners() {
    // 监听PubSub购物车更新事件
    if (typeof subscribe !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe('cart-update', (event) => {
        if (this.classList.contains('active')) {
          this.refreshCartContents();
        }
      });
    }

    // 监听自定义购物车更新事件
    document.addEventListener('cart:updated', () => {
      if (this.classList.contains('active')) {
        this.refreshCartContents();
      }
    });

    // 监听购物车项目添加事件  
    document.addEventListener('cart:item-added', () => {
      // 延迟一点时间确保服务器端购物车已更新
      setTimeout(() => {
        if (this.classList.contains('active')) {
          this.refreshCartContents();
        }
      }, 100);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    // 移除原有的href属性，防止页面跳转
    cartLink.removeAttribute('href');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.style.cursor = 'pointer';

    // 移除可能存在的其他点击事件监听器
    const newCartLink = cartLink.cloneNode(true);
    cartLink.parentNode.replaceChild(newCartLink, cartLink);

    // 添加我们的点击事件
    newCartLink.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.open(newCartLink);
    });

    newCartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        event.stopPropagation();
        this.open(newCartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // 防止页面抖动：计算滚动条宽度并设置padding补偿
    this.preventLayoutShift();

    // 在打开购物车抽屉之前，先缓存推荐容器内容
    this.cacheRecommendations();

    // 在打开购物车抽屉时刷新购物车内容
    this.refreshCartContents();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    // 恢复页面布局，移除padding补偿
    this.restoreLayoutShift();

    document.body.classList.remove('overflow-hidden');
  }

  preventLayoutShift() {
    try {
      // 计算滚动条宽度
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      if (scrollbarWidth > 0) {
        // 使用CSS自定义属性设置滚动条宽度
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      }
    } catch (e) {
      // 静默处理错误，不影响用户体验
    }
  }

  restoreLayoutShift() {
    try {
      // 移除CSS自定义属性
      document.documentElement.style.removeProperty('--scrollbar-width');
    } catch (e) {
      // 静默处理错误，不影响用户体验
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshCartContents() {
    // 获取最新的购物车状态
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // 更新购物车图标数量
        this.updateCartIcon(cart);

        // 如果购物车为空，显示空购物车状态并加载推荐
        if (cart.item_count === 0) {
          this.classList.add('is-empty');
          // 空购物车时也需要重新初始化推荐组件
          this.fetchCartDrawerContent();
        } else {
          // 购物车不为空，获取购物车抽屉的最新HTML
          this.classList.remove('is-empty');
          this.fetchCartDrawerContent();
        }
      })
      .catch(error => {
        console.error('Error refreshing cart contents:', error);
      });
  }

  updateCartIcon(cart) {
    // 更新购物车图标中的数量
    const cartIconBubble = document.querySelector('#cart-icon-bubble');
    if (cartIconBubble) {
      const countElement = cartIconBubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (countElement) {
        countElement.textContent = cart.item_count;
      }

      const visuallyHiddenCount = cartIconBubble.querySelector('.cart-count-bubble .visually-hidden');
      if (visuallyHiddenCount) {
        visuallyHiddenCount.textContent = cart.item_count === 1
          ? `${cart.item_count} item`
          : `${cart.item_count} items`;
      }
    }
  }

  fetchCartDrawerContent() {
    // 获取购物车抽屉的最新内容
    fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(html, 'text/html');
        const newDrawerInner = newDoc.querySelector('.drawer__inner');

        if (newDrawerInner) {
          const currentDrawerInner = this.querySelector('.drawer__inner');
          if (currentDrawerInner) {
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;

            // 恢复缓存的推荐内容
            this.restoreCachedRecommendations();
          }
        }
      })
      .catch(error => {
        console.error('Error fetching cart drawer content:', error);
      });
  }


  cacheRecommendations() {
    // 缓存推荐容器的完整内容（包括已加载的推荐产品）
    const recommendationContainers = this.querySelectorAll('#cart-recommendations-cart-drawer-recommendations');

    if (recommendationContainers.length > 0) {
      this.cachedRecommendations = Array.from(recommendationContainers).map(container => {
        // 检查推荐产品是否已加载（swiper-wrapper 中有 swiper-slide）
        const swiperWrapper = container.querySelector('.swiper-wrapper');
        const hasSlides = swiperWrapper && swiperWrapper.querySelectorAll('.swiper-slide').length > 0;

        return {
          id: container.id,
          parentSelector: this.getRecommendationParentSelector(container),
          html: container.parentElement.cloneNode(true),
          hasContent: hasSlides
        };
      });

      console.log('Cached recommendations:', this.cachedRecommendations.map(c => ({ id: c.id, hasContent: c.hasContent })));
    }
  }

  getRecommendationParentSelector(container) {
    // 获取推荐容器父元素的选择器（通常是 .cart-drawer-recommendations）
    let parent = container.parentElement;
    if (parent && parent.classList.length > 0) {
      return '.' + Array.from(parent.classList).join('.');
    }
    return null;
  }

  restoreCachedRecommendations() {
    // 恢复缓存的推荐内容
    if (!this.cachedRecommendations || this.cachedRecommendations.length === 0) {
      // 如果没有缓存，初始化推荐
      return;
    }
    this.cachedRecommendations.forEach(cached => {
      const container = this.querySelector(`#${cached.id}`);
      if (!container) return;

      // 只有当缓存中有内容时才恢复，否则重新初始化
      if (cached.html && cached.hasContent) {
        console.log(`Restoring cached recommendations for ${cached.id}`);
        // 找到对应的父容器并替换内容
        let parent = container.parentElement;
        if (parent) {
          parent.replaceWith(cached.html.cloneNode(true));

          // 替换后重新初始化 Swiper
          setTimeout(() => {
            const newContainer = this.querySelector(`#${cached.id}`);
            if (!newContainer) {
              console.warn(`Container ${cached.id} not found after replacement`);
              return;
            }

            // 检查 Swiper 库是否已加载
            if (typeof Swiper === 'undefined') {
              console.warn('Swiper library not found');
              return;
            }

            // 从 data 属性获取配置
            const slidesPerView = parseInt(newContainer.dataset.slidesPerView) || 2;
            const showScrollbar = newContainer.dataset.showScrollbar === 'true';

            // 查找 scrollbar 元素（相对于新容器）
            const scrollbarEl = newContainer.querySelector('.swiper-scrollbar');
            const prevButton = newContainer.querySelector('.swiper-button-prev');
            const nextButton = newContainer.querySelector('.swiper-button-next');

            // Swiper 配置
            const swiperConfig = {
              slidesPerView: slidesPerView,
              spaceBetween: 20,
              grabCursor: true,
              breakpoints: {
                320: {
                  slidesPerView: 2,
                  spaceBetween: 10,
                },
                768: {
                  slidesPerView: 3,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView: slidesPerView,
                  spaceBetween: 20,
                }
              }
            };

            // 如果使用滚动条
            if (showScrollbar && scrollbarEl) {
              swiperConfig.scrollbar = {
                el: scrollbarEl,
                draggable: true,
              };
            }
            // 否则使用导航箭头
            else if (prevButton && nextButton) {
              swiperConfig.navigation = {
                nextEl: nextButton,
                prevEl: prevButton,
              };
            }

            try {
              new Swiper(newContainer, swiperConfig);
              console.log(`Swiper initialized for ${cached.id}`);
            } catch (error) {
              console.error(`Error initializing Swiper for ${cached.id}:`, error);
            }
          }, 100);
        }
      } else {
        // 如果缓存中没有内容，重新初始化推荐
        console.log(`No cached content for ${cached.id}, reinitializing...`);
      }
    });
  }

  loadRecommendations() {
    const recommendationsContainer = this.querySelector('#cart-recommendations');
    if (!recommendationsContainer || recommendationsContainer.children.length) return;

    // 尝试获取产品推荐，如果没有特定产品则获取热门产品
    const productId = this.getRandomProductId();
    let apiUrl = '/recommendations/products.json?limit=4';

    if (productId) {
      apiUrl += `&product_id=${productId}`;
    }

    fetch(apiUrl)
      .then(response => response.json())
      .then(data => {
        if (data.products && data.products.length > 0) {
          this.renderRecommendations(data.products.slice(0, 4), recommendationsContainer);
        } else {
          // 如果推荐API没有返回结果，回退到获取一般产品
          return fetch('/products.json?limit=4');
        }
      })
      .then(response => {
        if (response) {
          return response.json();
        }
      })
      .then(data => {
        if (data && data.products && data.products.length > 0) {
          this.renderRecommendations(data.products.slice(0, 4), recommendationsContainer);
        }
      })
      .catch(error => {
        console.error('Error loading recommendations:', error);
        // 显示默认的推荐内容
        this.renderFallbackRecommendations(recommendationsContainer);
      });
  }

  getRandomProductId() {
    // 从最近浏览的产品中获取ID，如果没有则返回null
    const recentlyViewed = localStorage.getItem('recently-viewed-products');
    if (recentlyViewed) {
      try {
        const products = JSON.parse(recentlyViewed);
        if (products.length > 0) {
          return products[Math.floor(Math.random() * products.length)];
        }
      } catch (e) {
        console.error('Error parsing recently viewed products:', e);
      }
    }
    return null;
  }

  renderRecommendations(products, container) {
    const productCategories = {
      'furniture': 'Furniture',
      'chairs': 'Chairs&Stools',
      'sofa': 'Sofa',
      'table': 'Table Decor'
    };

    container.innerHTML = products.map(product => {
      const category = this.getProductCategory(product, productCategories);
      const imageUrl = product.images && product.images[0]
        ? product.images[0].replace('.jpg', '_300x300.jpg').replace('.png', '_300x300.png')
        : '';

      return `
        <a href="/products/${product.handle}" class="recommendation-item">
          <img src="${imageUrl}" alt="${product.title}" class="recommendation-item__image" loading="lazy">
          <div class="recommendation-item__title">${product.title}</div>
        </a>
      `;
    }).join('');
  }

  getProductCategory(product, categories) {
    const productType = product.product_type ? product.product_type.toLowerCase() : '';
    const tags = product.tags ? product.tags.map(tag => tag.toLowerCase()) : [];

    // 根据产品类型或标签匹配分类
    for (const [key, value] of Object.entries(categories)) {
      if (productType.includes(key) || tags.some(tag => tag.includes(key))) {
        return value;
      }
    }

    return 'Furniture'; // 默认分类
  }

  renderFallbackRecommendations(container) {
    // 显示默认的推荐内容，如果API调用失败
    container.innerHTML = `
      <div class="recommendation-fallback">
        <p style="text-align: center; color: rgb(var(--color-foreground), 0.6); font-size: 1.4rem;">
          Discover our featured products
        </p>
        <a href="/collections/all-products" class="button" style="display: block; text-align: center; margin-top: 1rem;">
          Browse All Products
        </a>
      </div>
    `;
  }

  disconnectedCallback() {
    // 清理事件监听器
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

// 添加全局测试函数
window.testCartDrawer = function () {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    console.log('Cart drawer found, attempting to open...');
    cartDrawer.open();
  } else {
    console.error('Cart drawer not found in DOM');
  }
};

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
